import {
  HttpClient,
  HttpContext,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiTarget } from '../environment/api-environment.model';
import { SHARED_ENVIRONMENT } from '../environment/shared-environment.token';
import { BYPASS_API_ROUTING } from './api-routing.context';
import { resolveTargetBaseUrl } from './resolve-base-url';

export interface ApiRequestOptions {
  params?: HttpParams | Record<string, string | number | boolean | readonly (string | number | boolean)[]>;
  headers?: HttpHeaders | Record<string, string | string[]>;
  context?: HttpContext;
  withCredentials?: boolean;
}

/**
 * One split API service, addressed directly.
 *
 * Two surfaces, on purpose:
 *
 * - `baseUrl` lets an existing service swap its `adminApiUrl` field for a client without
 *   touching any of its call sites. This is the migration path for the 34 services that
 *   still build URLs by hand.
 * - `get`/`post`/`put`/`delete`/`patch` take a path relative to the service and are what new
 *   code should use. Requests issued this way carry {@link BYPASS_API_ROUTING}.
 *
 * Calls needing `observe: 'response'`, `responseType: 'blob'` or other `HttpClient` overloads
 * should use `baseUrl` with `HttpClient` directly rather than widening this surface.
 */
export abstract class TargetApiClient {
  private readonly http = inject(HttpClient);
  private readonly environment = inject(SHARED_ENVIRONMENT);

  protected constructor(private readonly target: ApiTarget) {}

  /** Origin for this service, with no trailing slash. */
  get baseUrl(): string {
    return resolveTargetBaseUrl(this.target, this.environment);
  }

  /** Absolute URL for a path relative to this service. */
  urlFor(path: string): string {
    const relativePath = path.replace(/^\/+/, '');
    return relativePath ? `${this.baseUrl}/${relativePath}` : this.baseUrl;
  }

  get<T>(path: string, options?: ApiRequestOptions): Observable<T> {
    return this.http.get<T>(this.urlFor(path), this.withBypass(options));
  }

  post<T>(path: string, body: unknown, options?: ApiRequestOptions): Observable<T> {
    return this.http.post<T>(this.urlFor(path), body, this.withBypass(options));
  }

  put<T>(path: string, body: unknown, options?: ApiRequestOptions): Observable<T> {
    return this.http.put<T>(this.urlFor(path), body, this.withBypass(options));
  }

  patch<T>(path: string, body: unknown, options?: ApiRequestOptions): Observable<T> {
    return this.http.patch<T>(this.urlFor(path), body, this.withBypass(options));
  }

  delete<T>(path: string, options?: ApiRequestOptions): Observable<T> {
    return this.http.delete<T>(this.urlFor(path), this.withBypass(options));
  }

  private withBypass(options?: ApiRequestOptions): ApiRequestOptions {
    const context = (options?.context ?? new HttpContext()).set(BYPASS_API_ROUTING, true);
    return { ...options, context };
  }
}
